// ★★ WI — OS CAMPOS DO COMPARECIMENTO, num lugar só. Puro.
//
// ★ POR QUE ESTE MÓDULO EXISTE: até a WI, a escrita destes campos morava DENTRO de
// `moverEtapa` (`services/leadsService.js`), e só o funil passava por lá. A WI abriu o
// segundo caminho — a baixa "Compareceu" pela AGENDA e pela FICHA, que grava pelo
// `registrarComparecimento` (`services/agendamentosService.js`). Dois caminhos para o
// mesmo fato, e a decisão do dono foi explícita: **copiar está proibido**.
//
// ⚠ O DANO SE ISTO DIVERGIR — e é o motivo de o dono ter proibido a cópia: a marca
// `semOrcamento` tem uma LIMPEZA (valor > 0 derruba a marca). Uma cópia que esqueça a
// limpeza deixa o lead com valor E com a marca ao mesmo tempo, e o contador
// `compareceuSemOrcamento` das Metas mente **para sempre** — sem ninguém perceber,
// porque nenhuma tela mostra os dois campos lado a lado.
//
// ★ O CARIMBO ENTRA POR PARÂMETRO. `serverTimestamp` é sentinela do Firestore, não
// valor: recebê-lo de fora é o que mantém este módulo puro e testável sem emulador,
// como `regras.js` e `agenda/baixa.js`. Quem chama passa `serverTimestamp`; o teste
// passa uma função qualquer e vê O QUE foi carimbado, não o valor.
import { paraNumero } from '@/leads/recebimentos';

const texto = (v) => String(v ?? '').trim();
const naoNegativo = (v) => Math.max(0, paraNumero(v));

/**
 * Os campos que uma entrada em "Compareceu" (ou um fechamento que colete os mesmos
 * dados) muda no lead. Devolve SÓ as chaves que mudam — nunca um documento inteiro.
 *
 * @param lead      o lead como está hoje (para a limpeza e para não retroceder carimbos)
 * @param dados     o que a tela coletou: codigoCareflow, valorOrcado, semOrcamento, nomeConfirmado
 * @param novaEtapa a etapa de destino — `compareceu` carimba `compareceuEm`; `fechado` também,
 *                  se não houver prova anterior (PC-5)
 * @param autor     { id, nome } de quem gravou, para os carimbos de autoria
 * @param carimbo   a função que produz o timestamp (na produção, `serverTimestamp`)
 * @param agendamentosAntes PC-5: os agendamentos do lead lidos ANTES do lote — obrigatório
 *                  quando `novaEtapa === 'fechado'` (decide se o fechamento carimba `compareceuEm`)
 */
export function camposDoComparecimento(lead = null, dados = {}, { novaEtapa = null, autor = null, carimbo, agendamentosAntes } = {}) {
  if (typeof carimbo !== 'function') {
    throw new Error('camposDoComparecimento: `carimbo` é obrigatório (na produção, serverTimestamp).');
  }
  const campos = {};
  const quem = { PorId: autor?.id ?? 'sistema', PorNome: autor?.nome ?? 'Sistema' };

  // VA — o código é o que faz a importação casar por código; o nome vira fallback.
  if (dados.codigoCareflow !== undefined) campos.codigoCareflow = texto(dados.codigoCareflow);

  /**
   * ★★★ WY (09/out/2026) — O NEGOCIADOR, e esta é a linha que faltava.
   *
   * ⚠⚠⚠ O DEFEITO, medido e reproduzido (`docs/medicao-comparecimento-permissao.md`): o
   * diálogo da baixa **coleta** o negociador, **valida** que ele foi escolhido
   * (`DialogoBaixaComparecimento:102`) e o **emite** em `dados` — ★★★ e esta função **o
   * descartava**. O dado chegava até a porta e não entrava.
   *
   * ★★ E a regra do Firestore o exige: `entrandoEmNegociacao() → temNegociador(...)`
   * (`firestore.rules:359`, do WO lote 3c). ⚠⚠ Em Firestore rules, **acessar propriedade
   * inexistente é ERRO DE AVALIAÇÃO, não `false`** — e erro de avaliação **nega**. Daí a
   * mensagem que a Jéssica viu:
   * `PERMISSION_DENIED … Property negociadorId is undefined on object. @ L354`.
   *
   * ★★★ Reproduzido no emulador com as rules de produção: **SDR e GESTOR levam o MESMO erro**
   * — ⚠ não era problema de papel. E **612 dos 614** leads em `agendado` (99,7%) estavam
   * nessa situação, porque quase nenhum tem negociador antes da baixa.
   *
   * ★ Por que no molde do `codigoCareflow` e não "sempre": ⚠ a baixa **"faltou"** não coleta
   * nada e chama esta função sem `dados` — gravar `negociadorId: null` ali **não resolveria
   * nada** (a regra pede string não vazia) e **apagaria** o negociador de um lead que já tem.
   *
   * ⚠⚠ `|| null` e não `?? null`: string vazia **tem** de virar `null`. ★ Deixar `''` seria
   * gravar um campo que existe e não serve — e a regra recusaria do mesmo jeito, agora com
   * `size() > 0` em vez de "undefined". ★★ Quem impede o vazio de chegar aqui é o diálogo.
   */
  if (dados.negociadorId !== undefined) {
    campos.negociadorId = dados.negociadorId || null;
    campos.negociadorNome = dados.negociadorNome ?? '';
  }

  /**
   * VE/WD-c1 — a OPORTUNIDADE. `valorOrcadoEm` só é carimbado quando há valor E o lead
   * ainda não tinha data, pela razão do dono: **"a data da oportunidade é a data em que a
   * pessoa COMPARECEU, não a data em que alguém digitou o valor depois"**. Preencher o
   * valor no fechamento não muda o mês da oportunidade no relatório.
   */
  if (dados.valorOrcado !== undefined) {
    const orcado = naoNegativo(dados.valorOrcado);
    campos.valorOrcado = orcado;
    if (orcado > 0 && !lead?.valorOrcadoEm) campos.valorOrcadoEm = carimbo();
  }

  /**
   * ★★ WH — A MARCA "COMPARECEU SEM ORÇAMENTO", E A LIMPEZA DELA.
   *
   * ★ A limpeza é a metade que ninguém lembra, e é a razão desta função existir: quando
   * um valor > 0 entra, a marca CAI. Só `=== true` marca; carimbo e autor acompanham,
   * como toda decisão do funil.
   */
  const valorNovo = dados.valorOrcado !== undefined
    ? naoNegativo(dados.valorOrcado)
    : naoNegativo(lead?.valorOrcado);
  if (dados.semOrcamento === true && !(valorNovo > 0)) {
    campos.semOrcamento = true;
    campos.semOrcamentoEm = carimbo();
    campos.semOrcamentoPorId = quem.PorId;
    campos.semOrcamentoPorNome = quem.PorNome;
  } else if (lead?.semOrcamento === true || dados.semOrcamento === false) {
    campos.semOrcamento = false;
  }

  /**
   * ★★ WH — o CARIMBO da confirmação do nome. A confirmação é um GESTO (a medição provou
   * que não dá para julgar se um nome está atualizado). O carimbo transforma o gesto em
   * registro. ★ E o limite, dito onde o código mora: a caixa pode ser marcada sem ler —
   * o carimbo registra o ato, não a verdade.
   */
  if (dados.nomeConfirmado === true) {
    campos.nomeConfirmadoEm = carimbo();
    campos.nomeConfirmadoPorId = quem.PorId;
    campos.nomeConfirmadoPorNome = quem.PorNome;
  }

  /**
   * VE — `compareceuEm` responde "este lead chegou a sentar na cadeira?". NÃO retrocede:
   * vale o PRIMEIRO comparecimento, senão uma segunda consulta empurraria o lead para o
   * mês em que ela aconteceu — e a comissão (`remuneracao/calculo.js`) lê exatamente este
   * campo.
   */
  if (novaEtapa === 'compareceu' && !lead?.compareceuEm) campos.compareceuEm = carimbo();

  /**
   * ★★★ PC-5 (22/out/2026) — TODO FECHAMENTO CONTA UM COMPARECIMENTO.
   *
   * Palavras do dono (18/out): *"se fechou quer dizer que o lead esteve na clínica. Se estiver
   * sem SDR não tem problema"*. Até aqui fechar de `novo`/`nao_agendou`/`nao_compareceu` não
   * contava em lugar nenhum, e fechar com avaliação aberta contava NA DATA DA CONSULTA — futura,
   * ou de meses atrás (medido: 566 leads com avaliação aberta até setembro). Ver
   * `docs/medicao-pc5-fechamento-comparecimento.md`.
   *
   * ★ A data é a do FECHAMENTO (o mesmo `carimbo` de `dataFechamento`): `compareceuEm` vence a
   * reserva da agenda em `dataComparecimento`, então a consulta baixada pelo lote não decide mês.
   *
   * ⚠⚠⚠ A GUARDA (b), e ela é DINHEIRO: quem já tem agendamento `compareceu` JÁ CONTA, no mês
   * daquela consulta. Carimbar aqui moveria o comparecimento para o mês do fechamento — e se o
   * mês antigo é setembro, já pago, pagaria DUAS VEZES (medido: 65 leads em `nao_fechou` assim).
   *
   * ⚠⚠ `agendamentosAntes` é o estado de ANTES do lote. A avaliação que o próprio lote vai
   * baixar ainda está `agendado` ali — e é isso que a deixa fora da prova. Passar a lista
   * depois da baixa faria todo fechamento com avaliação aberta deixar de carimbar.
   *
   * ★ Falha ALTO sem a lista: sem saber se há prova anterior, carimbar pode pagar duas vezes e
   * não carimbar descumpre a regra — nenhum dos dois é calado.
   */
  if (novaEtapa === 'fechado' && !lead?.compareceuEm) {
    if (!Array.isArray(agendamentosAntes)) {
      throw new Error('camposDoComparecimento: ao fechar, `agendamentosAntes` (do lead, antes do lote) é obrigatório.');
    }
    const jaCompareceu = agendamentosAntes.some((a) => a?.status === 'compareceu');
    if (!jaCompareceu) campos.compareceuEm = carimbo();
  }

  return campos;
}
